import { useCallback, useEffect, useState } from 'react'
import type { PositionPage } from '../../api/statisticsTypes'

const PAGE_SIZE = 50

/**
 * Legacy `PagedListHandler`/`paged-list-navigator`: an opaque, server-issued
 * `start` cursor per page (Datastore query cursor or a BigQuery offset — the
 * frontend never inspects it). Prev/Next only; the cursor stack lets "Prev"
 * go back without a second request.
 */
export function useCursorPage<T>(
  fetcher: (pageSize: number, start: string | undefined) => Promise<PositionPage<T>>,
  deps: unknown[],
) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [pageIndex, setPageIndex] = useState(0)
  const [items, setItems] = useState<T[]>([])
  const [hasNext, setHasNext] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  // Any filter change resets to page 1.
  useEffect(() => {
    setCursors([undefined])
    setPageIndex(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetcher(PAGE_SIZE, cursors[pageIndex])
      .then((page) => {
        if (cancelled) return
        setItems(page.items)
        setHasNext(page.hasNext)
        setError(null)
        const nextCursor = page.cursor == null ? undefined : String(page.cursor)
        if (nextCursor && cursors.length === pageIndex + 1) {
          setCursors((prev) => [...prev, nextCursor])
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err ?? true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, cursors[pageIndex], ...deps])

  const next = useCallback(() => setPageIndex((i) => i + 1), [])
  const prev = useCallback(() => setPageIndex((i) => Math.max(0, i - 1)), [])

  return { items, loading, error, page: pageIndex + 1, hasPrev: pageIndex > 0, hasNext, next, prev, pageSize: PAGE_SIZE }
}
